import { z } from 'zod';

import type { UserRole } from '~/api/types';
import { USER_ROLES, type UserRoleValue } from '~/features/auth/roles';
import { DOMAINS } from '~/features/navigation/domains';

const nameRegex = /^[\w@#\-()*'" ]+$/;

export const validRoles: UserRoleValue[] = [
  USER_ROLES.AbbottUser,
  USER_ROLES.AdminAbbott,
  USER_ROLES.NonHcpAdmin,
  USER_ROLES.HcpAdmin,
  USER_ROLES.HcpUser,
];

const REQUIRED_DEPARTMENT_ROLES: UserRoleValue[] = [USER_ROLES.HcpUser];

export const REQUIRED_IDN_ROLES: UserRoleValue[] = [
  USER_ROLES.HcpAdmin,
  USER_ROLES.HcpUser,
  USER_ROLES.NonHcpAdmin,
];

const ALLOWED_ABBOTT_EMAIL_DOMAINS = ['abbott.com'];

export const basicUserSchema = z.object({
  idnId: z.string().optional(),
  firstName: z
    .string()
    .min(1, 'First Name is required')
    .regex(nameRegex, 'Should be valid')
    .max(40, 'First Name is too long'),
  lastName: z
    .string()
    .min(1, 'Last Name is required')
    .regex(nameRegex, 'Should be valid')
    .max(80, 'Last Name is too long'),
  email: z.email(),
  phone: z
    .string()
    .optional()
    .refine(
      (val) => {
        console.log(val);

        return val && z.e164().safeParse(val).success;
      },
      { message: 'Invalid phone number' },
    ),
  role: z.custom<UserRole>().optional(),
  departmentId: z.string().nullable().optional(),
});

const requiredSchema = basicUserSchema.safeExtend({
  role: z.custom<UserRole>().refine((role) => validRoles.includes(role), {
    message: 'Role is required',
  }),
});

export const userSchema = requiredSchema.refine(
  (data) => {
    if (REQUIRED_DEPARTMENT_ROLES.includes(data.role)) {
      return (
        typeof data.departmentId === 'string' && data.departmentId.trim() !== ''
      );
    }

    return true;
  },
  {
    message: 'Department is required',
    path: ['departmentId'],
  },
);

export const abbottAdminSchema = requiredSchema.safeExtend({
  email: z.email().refine(
    (email) => {
      const emailDomain = email.split('@')[1]?.toLowerCase() ?? '';

      return ALLOWED_ABBOTT_EMAIL_DOMAINS.includes(emailDomain);
    },
    {
      message: 'Email must end in @abbott.com',
    },
  ),
});

export const schemaMap = {
  [DOMAINS.ABBOTT_ADMINISTRATION]: abbottAdminSchema,
  [DOMAINS.USER_ADMINISTRATION]: userSchema,
  [DOMAINS.CUSTOMER_ADMINISTRATION]: userSchema,
  [DOMAINS.EDUCATION]: userSchema,
  [DOMAINS.OUTPATIENT]: userSchema,
} as const;
